import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IntakeFormTemplate } from '../../../types/formBuilder';
import { getFormById, saveFormSubmission, getFormSubmission } from '../../../firebase/formManagement';
import { DynamicFormRenderer } from './DynamicFormRenderer';

interface DynamicIntakeFormModalProps {
  formId: string;
  caseId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  readOnly?: boolean;
}

export function DynamicIntakeFormModal({ 
  formId, 
  caseId, 
  isOpen, 
  onClose,
  onSuccess,
  readOnly = false
}: DynamicIntakeFormModalProps) {
  const { t, i18n } = useTranslation();
  
  const [formTemplate, setFormTemplate] = useState<IntakeFormTemplate | null>(null);
  const [initialAnswers, setInitialAnswers] = useState<{ [fieldName: string]: any }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    // Load user from localStorage
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen && formId && caseId) {
      loadFormAndSubmission();
    }
  }, [isOpen, formId, caseId]);

  const loadFormAndSubmission = async () => {
    setIsLoading(true);
    try {
      // Load form template
      const form = await getFormById(formId);
      if (!form) {
        alert(t('intakeForms.formNotFound'));
        onClose();
        return;
      }
      
      // Check permissions - verify user can access this form
      if (user) {
        const userOrgRole = user.organizationRole;
        const isSuperAdmin = user.role === 'super_admin';
        
        // Super admins can access all forms
        if (!isSuperAdmin && userOrgRole) {
          // If form is shared with 'specific', check allowedRoles
          if (form.sharedWith === 'specific') {
            // If allowedRoles is specified, check if user's role is included
            if (form.allowedRoles && form.allowedRoles.length > 0) {
              if (!form.allowedRoles.includes(userOrgRole)) {
                alert(t('intakeForms.noPermission') || 'You do not have permission to access this form.');
                onClose();
                return;
              }
            }
          }
          // If sharedWith is 'all', allow access (no need to check)
        }
      }
      
      setFormTemplate(form);

      // Load existing submission if any
      const submission = await getFormSubmission(formId, caseId);
      if (submission) {
        setInitialAnswers(submission.answers || {});
      }
    } catch (error) {
      console.error('Error loading form:', error);
      alert(t('intakeForms.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (answers: { [fieldName: string]: any }) => {
    // If read-only, prevent submission
    if (readOnly) {
      return;
    }

    if (!formId || !caseId || !user) {
      alert(t('intakeForms.missingInfo'));
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveFormSubmission(
        formId,
        caseId,
        answers,
        user.email,
        'completed'
      );

      if (result.success) {
        // Log activity
        const { addActivityNote } = await import('../../../firebase');
        await addActivityNote(
          caseId,
          `${t('intakeForms.completed')}: ${formTemplate?.name}`,
          'completed',
          user.email
        );

        alert(t('intakeForms.saveSuccess'));
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        alert(result.error || t('intakeForms.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving form:', error);
      alert(t('intakeForms.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const formName = i18n.language === 'he' ? (formTemplate?.nameHe || formTemplate?.name) : (formTemplate?.nameEn || formTemplate?.name);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
        direction: i18n.language === 'he' ? 'rtl' : 'ltr'
      }}>
        {/* Fixed Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 30px',
          borderBottom: '1px solid #e9ecef',
          background: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ 
              color: '#000000', 
              margin: '0 0 8px 0',
              fontSize: '20px'
            }}>
              {formName}
            </h2>
            {formTemplate?.description && (
              <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>
                {formTemplate.description}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
              marginLeft: i18n.language === 'he' ? '0' : '20px',
              marginRight: i18n.language === 'he' ? '20px' : '0'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
          >
            ×
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div style={{
          padding: '30px',
          overflowY: 'auto',
          flex: 1
        }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#666666' }}>
                {t('common.loading')}...
              </div>
            </div>
          ) : formTemplate ? (
            <DynamicFormRenderer
              formTemplate={formTemplate}
              initialValues={initialAnswers}
              onSubmit={handleSubmit}
              submitButtonText={isSaving ? t('common.saving') : t('common.save')}
              isSubmitting={isSaving}
              readOnly={readOnly}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <div style={{ fontSize: '18px', color: '#666666' }}>
                {t('intakeForms.formNotFound')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DynamicIntakeFormModal;



